/**
 * arrowDescriptionTemplate - PIPH-90 / PIPH-319 Curved Arrow Description Template
 *
 * Generates deterministic verbal descriptions for curved arrows in mechanism
 * diagrams. Template output is used in SVG navigation (arrow element labels)
 * and as context for the Piph Author LLM.
 *
 * Template format: "{arrow type} from {source} to {destination}"
 *
 * Atom naming: arrow endpoints don't sit naturally inside a single feature
 * scope (an arrow may cross feature boundaries), so we don't pass a
 * `featureContextId` to `getAtomNameWithChargeContext`. The wrapper falls
 * back to the atom's default feature (highest type priority by
 * ring > bridged_bicyclic > chain > atom_cluster > functional_group > atom),
 * giving each atom a stable name regardless of the arrow being described.
 *
 * PIPH-319 brevity rules: a per-description `BrevityCtx` tracks atom mentions
 * so that:
 *   - Rule 1: "oxygen of Oxygen" collapses to "oxygen" (feature qualifier
 *     equals element name).
 *   - Rule 2: the same atom mentioned again drops its charge prefix AND
 *     "of {feature}" suffix.
 *   - Rule 5: when both atoms of a bond share a feature, "of {feature}" is
 *     attached to the joined phrase once rather than to each atom.
 *
 * @package @alchemiesolns/svg-interactive
 * @see PIPH-90, PIPH-319
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { ArrowIdDef } from '@alchemiesolns/arrow-def';
import { ArrowType } from '@alchemiesolns/arrow-def';
import type { SerializationResult } from '@alchemiesolns/feature-extractor';
import {
  type BrevityCtx,
  createBrevityCtx,
  renderAtomName,
  renderBondAtoms,
} from './atomNamingBrevity';

/**
 * Get the arrow type label for display.
 *
 * Rule: types starting with 'r' (radical) or 'c' (coordinate bond) → "Fishhook arrow"
 * Everything else (pa, pb, ba, bb) → "Curved arrow"
 */
function getArrowTypeLabel(type: ArrowType): string {
  switch (type) {
    case ArrowType.RtoAtom:
    case ArrowType.RtoBond:
    case ArrowType.CBondToAtom:
    case ArrowType.CBondToBond:
      return 'Fishhook arrow';
    case ArrowType.LPtoAtom:
    case ArrowType.LPtoBond:
    case ArrowType.BondToAtom:
    case ArrowType.BondToBond:
    default:
      return 'Curved arrow';
  }
}

/**
 * Get the bond order phrase. Single bonds get no qualifier, double/triple get one.
 */
function getBondOrderPrefix(order: number): string {
  if (order === 2) return 'double ';
  if (order === 3) return 'triple ';
  return ''; // Single bonds: no qualifier
}

/**
 * Describe a bond source with both atom endpoints.
 *
 * Default ordering: anchor atom first — the anchor is the atom that stays
 * connected, and listing it first gives natural reading direction toward the
 * departing electron.
 *
 * PIPH-319 refinement: when the anchor is also the arrow's destination (the
 * electrons land on the anchor itself, e.g. a `ba` arrow `bond C-Br → Br`
 * with anchor=Br), flip ordering and list the **non-anchor** atom first so
 * the bond reads as "from bond between {context} and {recipient} to
 * {recipient}" — keeping the context atom up front.
 */
function describeBondSource(
  ctx: BrevityCtx,
  bondId: string,
  anchorId: string | undefined,
  destinationId: string | undefined,
): string {
  const bond = ctx.workspace.getBond(bondId);
  if (!bond) return 'bond';

  const orderPrefix = getBondOrderPrefix(bond.order);

  const anchorIsDestination = anchorId !== undefined && anchorId === destinationId;

  // Pick the "first-listed" atom: usually the anchor, but the non-anchor
  // when the anchor is also the destination.
  let atom1Id: string;
  let atom2Id: string;
  if (anchorId && anchorIsDestination) {
    // Non-anchor first.
    if (anchorId === bond.a1.id) {
      atom1Id = bond.a2.id;
      atom2Id = bond.a1.id;
    } else {
      atom1Id = bond.a1.id;
      atom2Id = bond.a2.id;
    }
  } else if (anchorId && anchorId === bond.a2.id) {
    // Anchor first (anchor is the second atom in the bond).
    atom1Id = bond.a2.id;
    atom2Id = bond.a1.id;
  } else {
    // Default: a1 first (either anchor is a1, or no anchor).
    atom1Id = bond.a1.id;
    atom2Id = bond.a2.id;
  }

  const atom1Index = ctx.atomIdToIndex.get(atom1Id);
  const atom2Index = ctx.atomIdToIndex.get(atom2Id);

  return `${orderPrefix}bond between ${renderBondAtoms(ctx, atom1Index, atom2Index)}`;
}

/**
 * Describe a bond destination with both atom endpoints.
 */
function describeBondDestination(ctx: BrevityCtx, bondId: string): string {
  const bond = ctx.workspace.getBond(bondId);
  if (!bond) return 'bond';

  const orderPrefix = getBondOrderPrefix(bond.order);
  const atom1Index = ctx.atomIdToIndex.get(bond.a1.id);
  const atom2Index = ctx.atomIdToIndex.get(bond.a2.id);

  return `${orderPrefix}bond between ${renderBondAtoms(ctx, atom1Index, atom2Index)}`;
}

/**
 * Build the source description based on arrow type.
 *
 * - pa, pb: "lone pair on {atom}"
 * - ba, bb, cba, cbb: "bond between {atom} and {atom}"
 * - ra, rb: "radical on {atom}"
 */
function describeSource(ctx: BrevityCtx, arrow: ArrowIdDef): string {
  switch (arrow.type) {
    case ArrowType.LPtoAtom:
    case ArrowType.LPtoBond: {
      const atomIndex = ctx.atomIdToIndex.get(arrow.srcId);
      return `lone pair on ${renderAtomName(ctx, atomIndex)}`;
    }

    case ArrowType.RtoAtom:
    case ArrowType.RtoBond: {
      const atomIndex = ctx.atomIdToIndex.get(arrow.srcId);
      return `radical on ${renderAtomName(ctx, atomIndex)}`;
    }

    case ArrowType.BondToAtom:
    case ArrowType.BondToBond:
    case ArrowType.CBondToAtom:
    case ArrowType.CBondToBond:
      return describeBondSource(ctx, arrow.srcId, arrow.anchorId, arrow.dstId);

    default:
      return 'source';
  }
}

/**
 * Build the destination description based on arrow type suffix.
 *
 * - Types ending in 'a' (pa, ba, ra, cba): "to {atom}"
 * - Types ending in 'b' (pb, bb, rb, cbb): "to bond between {atom} and {atom}"
 */
function describeDestination(ctx: BrevityCtx, arrow: ArrowIdDef): string {
  switch (arrow.type) {
    case ArrowType.LPtoAtom:
    case ArrowType.BondToAtom:
    case ArrowType.RtoAtom:
    case ArrowType.CBondToAtom: {
      const atomIndex = ctx.atomIdToIndex.get(arrow.dstId);
      return renderAtomName(ctx, atomIndex);
    }

    case ArrowType.LPtoBond:
    case ArrowType.BondToBond:
    case ArrowType.RtoBond:
    case ArrowType.CBondToBond:
      return describeBondDestination(ctx, arrow.dstId);

    default:
      return 'destination';
  }
}

/**
 * Generate a verbal description of a curved arrow.
 *
 * Template: "{arrow type} from {source} to {destination}"
 *
 * @param arrow - The arrow definition with type, srcId, dstId, and optional anchorId
 * @param workspace - The molecular workspace containing atoms and bonds
 * @param serialized - The serialization result from serializeFeatures()
 * @param atomIdToIndex - Map from atom ID to index in serialized.atoms
 * @returns Formatted arrow description
 *
 * @example
 * // Lone pair to atom (pa)
 * describeArrow(arrow, workspace, serialized, atomIdToIndex)
 * // "Curved arrow from lone pair on nitrogen to carbon-3 of 3-carbon chain"
 *
 * @example
 * // Bond to atom (ba) with charged atom
 * describeArrow(arrow, workspace, serialized, atomIdToIndex)
 * // "Curved arrow from double bond between carbon-2 and carbon-3 to negatively charged oxygen"
 */
export function describeArrow(
  arrow: ArrowIdDef,
  workspace: MolecularWorkspace,
  serialized: SerializationResult,
  atomIdToIndex: Map<string, number>,
): string {
  const ctx = createBrevityCtx(workspace, serialized, atomIdToIndex);
  const arrowTypeLabel = getArrowTypeLabel(arrow.type);
  const source = describeSource(ctx, arrow);
  const destination = describeDestination(ctx, arrow);

  return `${arrowTypeLabel} from ${source} to ${destination}`;
}
